import React, { useState } from 'react';
import { 
  BookOpen, ChevronRight, ChevronDown, Search, Download, ExternalLink,
  Layers, Shield, Database, Brain, Users, GitBranch, Target, FileText,
  Zap, Upload, CheckCircle, BarChart3, AlertTriangle, Link2, Bot,
  Leaf, Building2, Globe, Scale, FileBarChart, Settings
} from 'lucide-react';
import { Badge } from '../components/ui/badge';

const UserGuide = () => {
  const [expandedSection, setExpandedSection] = useState('getting-started');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('guide');

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const sections = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: '🚀',
      content: [
        {
          heading: 'Welcome to Disclosure OS',
          text: 'Disclosure OS is an enterprise-grade ESG compliance platform - a complete operating system for sustainability disclosure. Our platform handles the entire ESG reporting lifecycle with autonomous AI agents, from data capture to audit-ready reports.'
        },
        {
          heading: 'Core Workflow',
          highlight: true,
          workflow: ['Upload', 'Map', 'Evidence', 'Claim', 'Audit Pack'],
          text: 'This is the defensibility flywheel that makes your ESG disclosures audit-ready and legally defensible.'
        },
        {
          heading: 'Quick Start Guide',
          steps: [
            'Log in with your credentials (role determines access level)',
            'Navigate to Data Foundation → Data Upload to ingest your ESG data',
            'Review AI extractions in AI Data Review (Human-in-the-Loop)',
            'Track compliance in Disclosure Engine → Framework Coverage',
            'Build claims with evidence in Claims & Assertions',
            'Generate audit packs in Evidence Vault → Audit Pack Builder'
          ]
        }
      ]
    },
    {
      id: 'architecture',
      title: 'Platform Architecture',
      icon: '🏗️',
      content: [
        {
          heading: 'Three-Layer Architecture',
          text: 'Disclosure OS is built on a modern three-layer architecture designed for enterprise scalability and audit defensibility.'
        },
        {
          heading: 'Experience Layer (UI)',
          layers: [
            { name: 'Command Center', desc: 'Executive dashboard with risk & readiness overview', icon: 'dashboard' },
            { name: 'Data Foundation', desc: 'Data ingestion, AI review, and consolidation', icon: 'database' },
            { name: 'Disclosure Engine', desc: 'Framework coverage, requirements, claims', icon: 'shield' },
            { name: 'Due Diligence OS', desc: 'CSDDD workflows, value chain risk, supplier portal', icon: 'gitbranch' },
            { name: 'Evidence Vault', desc: 'Evidence library, audit pack builder', icon: 'folder' },
            { name: 'Intelligence Layer', desc: 'Analytics, impact engine, predictions', icon: 'brain' },
            { name: 'Reporting Studio', desc: 'Report builder, XBRL export', icon: 'file' }
          ]
        },
        {
          heading: 'Domain Services (Core)',
          services: [
            'Entity & Value Chain Service - Organization hierarchy, suppliers, sites',
            'Framework & Requirements Service - All regulatory frameworks',
            'Metric/KPI Service - Definitions, values, validation',
            'Crosswalk Service - Multi-framework mappings',
            'Evidence Service - Document management, audit trails',
            'Claims & Reporting Service - Auditable assertions',
            'Workflow & Case Service - Tasks, SLAs, remediation'
          ]
        },
        {
          heading: 'Platform Services',
          services: [
            'Identity & RBAC - 7-role permission system',
            'Audit Log Service - Immutable compliance trail',
            'Real-time Collaboration - Live multi-user editing',
            'Integration Gateway - ERP connectors (SAP, Ariba, Coupa)'
          ]
        }
      ]
    },
    {
      id: 'roles',
      title: 'User Roles & Permissions',
      icon: '👥',
      content: [
        {
          heading: '7-Role RBAC System',
          text: 'Disclosure OS implements enterprise-grade Role-Based Access Control with 7 distinct roles, each with specific permissions aligned to ESG workflows.'
        },
        {
          heading: 'Available Roles',
          roles: [
            { name: 'Admin', color: 'red', permissions: 'Full system access, user management, configuration' },
            { name: 'Compliance Manager', color: 'purple', permissions: 'Framework management, report approval, claim verification' },
            { name: 'Data Analyst', color: 'blue', permissions: 'Data upload, KPI management, AI review approval' },
            { name: 'Legal Counsel', color: 'orange', permissions: 'Legal review, claim approval, CSDDD oversight' },
            { name: 'Policy Adviser', color: 'green', permissions: 'Policy documents, control management' },
            { name: 'Auditor', color: 'indigo', permissions: 'Read-only access, audit pack download, evidence review' },
            { name: 'Viewer', color: 'gray', permissions: 'Dashboard viewing, report access (read-only)' }
          ]
        },
        {
          heading: 'Real-time Collaboration',
          text: 'Multiple users can work simultaneously on the same data points. Changes are synced in real-time via WebSocket connections, with edit locks to prevent conflicts.'
        }
      ]
    },
    {
      id: 'frameworks',
      title: 'Supported Frameworks',
      icon: '📋',
      content: [
        {
          heading: 'Multi-Framework Compliance',
          text: 'Disclosure OS supports all major ESG reporting frameworks with intelligent crosswalk mappings. Enter data once, satisfy multiple frameworks.'
        },
        {
          heading: 'Primary Frameworks',
          frameworks: [
            { name: 'CSRD/ESRS', desc: 'Corporate Sustainability Reporting Directive - EU mandatory disclosure', status: 'Full Support' },
            { name: 'CSDDD', desc: 'Corporate Sustainability Due Diligence Directive - Value chain obligations', status: 'Full Support' },
            { name: 'EU Taxonomy', desc: 'Classification system for sustainable activities', status: 'Full Support' },
            { name: 'GRI Standards', desc: 'Global Reporting Initiative universal standards', status: 'Full Support' },
            { name: 'SASB', desc: 'Sector-specific sustainability metrics', status: 'Full Support' }
          ]
        },
        {
          heading: 'Additional Frameworks',
          frameworks: [
            { name: 'TCFD', desc: 'Task Force on Climate-related Financial Disclosures', status: 'Supported' },
            { name: 'TNFD', desc: 'Taskforce on Nature-related Financial Disclosures', status: 'Supported' },
            { name: 'SBTi', desc: 'Science Based Targets initiative', status: 'Supported' },
            { name: 'CDP', desc: 'Carbon Disclosure Project questionnaires', status: 'Supported' }
          ]
        },
        {
          heading: 'Crosswalk Mappings',
          text: 'Our Crosswalk Service automatically maps your KPIs to multiple framework requirements. When you enter emissions data, it simultaneously satisfies ESRS E1, GRI 305, TCFD metrics, CDP climate questions, and SBTi tracking.'
        }
      ]
    },
    {
      id: 'user-journeys',
      title: 'User Journeys',
      icon: '🗺️',
      content: [
        {
          heading: 'Journey A: CSRD/ESRS Reporting Cycle',
          journey: 'csrd',
          steps: [
            { step: 'Data Upload', desc: 'Upload Excel, PDF, or CSV files with ESG data' },
            { step: 'AI Classification', desc: 'System auto-tags content to ESRS topics (E1, S1, etc.)' },
            { step: 'Extraction & Normalization', desc: 'AI extracts KPI values, converts units, assigns periods' },
            { step: 'HITL Review', desc: 'Approve/adjust extractions in AI Data Review queue' },
            { step: 'KPI Publish', desc: 'Approved values become official records' },
            { step: 'Coverage Update', desc: 'Framework Coverage shows what\'s complete vs. gaps' },
            { step: 'Claims Drafting', desc: 'System proposes claims with linked evidence' },
            { step: 'Report Workflow', desc: 'Draft → Review → Legal → Board → Publish' },
            { step: 'Export', desc: 'Generate PDF, DOCX, or XBRL' }
          ]
        },
        {
          heading: 'Journey B: CSDDD Operational Cycle',
          journey: 'csddd',
          steps: [
            { step: 'Supplier Onboarding', desc: 'Add suppliers manually or import via ERP connectors' },
            { step: 'AI Risk Scoring', desc: 'Automatic risk tier based on country, commodity, history' },
            { step: 'Workflow Trigger', desc: 'Risk level triggers appropriate DD workflow' },
            { step: 'Evidence Collection', desc: 'Contracts, questionnaires, audits via Supplier Portal' },
            { step: 'Incident/Grievance', desc: 'Case management with SLA tracking' },
            { step: 'Remediation', desc: 'Corrective actions with verification evidence' },
            { step: 'Disclosure Update', desc: 'Auto-updates ESRS S2, GRI social topics, CDP' },
            { step: 'Audit Pack', desc: 'One-click evidence bundle for CSDDD claims' }
          ]
        },
        {
          heading: 'Journey C: EU Taxonomy Alignment',
          journey: 'taxonomy',
          steps: [
            { step: 'Activity Mapping', desc: 'Map revenue/CapEx/OpEx to eligible activities' },
            { step: 'Eligibility Check', desc: 'System generates checklist per activity' },
            { step: 'Alignment Workflow', desc: 'Upload evidence for TSC + DNSH + minimum safeguards' },
            { step: 'Calculation', desc: 'Compute eligible/aligned percentages' },
            { step: 'Claim Generation', desc: 'Claims with linked evidence per activity' },
            { step: 'Report Integration', desc: 'Taxonomy section auto-populates CSRD report' }
          ]
        },
        {
          heading: 'Journey D: Multi-Framework Reporting',
          journey: 'multi',
          steps: [
            { step: 'Select Targets', desc: 'Choose: CSRD + CDP + TCFD + SASB' },
            { step: 'Coverage Analysis', desc: 'See which KPIs satisfy multiple frameworks' },
            { step: 'Gap Identification', desc: 'Highlight what\'s uniquely required per framework' },
            { step: 'Fill Gaps', desc: 'Add only the incremental data needed' },
            { step: 'Compile Reports', desc: 'CSRD report, CDP response, TCFD pack, SASB metrics' }
          ]
        }
      ]
    },
    {
      id: 'workflow-automation',
      title: 'Workflow Automation',
      icon: '⚡',
      content: [
        {
          heading: 'Autonomous Workflows',
          text: 'Disclosure OS includes 12 pre-built workflow templates across 8 categories that automate routine ESG processes.'
        },
        {
          heading: 'Workflow Categories',
          categories: [
            { name: 'Data Quality', templates: ['Auto-Approve High Confidence Extractions', 'Daily Data Completeness Check'] },
            { name: 'Monitoring', templates: ['Emissions Anomaly Alert', 'Threshold Breach Action'] },
            { name: 'Compliance', templates: ['Compliance Deadline Reminder'] },
            { name: 'Reporting', templates: ['Weekly Progress Report', 'Monthly XBRL Sync', 'Report Distribution'] },
            { name: 'Impact', templates: ['KPI Change Impact Analysis'] },
            { name: 'Due Diligence', templates: ['High-Risk Supplier Escalation'] },
            { name: 'Claims', templates: ['Claim Approval Workflow'] },
            { name: 'Evidence', templates: ['Auto-Link Evidence to Claims'] }
          ]
        },
        {
          heading: 'Workflow Triggers',
          triggers: [
            'Data Upload - When new files are uploaded',
            'Threshold Breach - When KPIs exceed limits',
            'Compliance Deadline - Days before due dates',
            'Scheduled - Daily, weekly, monthly automation',
            'KPI Change - When values change significantly',
            'Supplier Risk Change - When risk scores update',
            'Claim Status Change - When claims move through workflow',
            'Evidence Uploaded - When new evidence is added',
            'Report Approved - When reports are finalized'
          ]
        },
        {
          heading: 'Available Actions',
          actions: [
            'Send Alert - Notify relevant stakeholders',
            'Create Task - Auto-generate action items',
            'Flag for Review - Route to HITL queue',
            'Auto Approve - Approve high-confidence items',
            'Send Email - Email notifications',
            'Escalate to Manager - Priority escalation',
            'Trigger Impact Analysis - Run downstream analysis',
            'Sync to XBRL - Update XBRL filing data',
            'Run Validation - Execute validation checks'
          ]
        }
      ]
    },
    {
      id: 'data-model',
      title: 'Data Model & Traceability',
      icon: '🔗',
      content: [
        {
          heading: 'Single Object Model',
          text: 'Disclosure OS uses a canonical schema that all frameworks map onto. No per-framework silos - enter once, use everywhere.'
        },
        {
          heading: 'Core Objects',
          objects: [
            { name: 'Entities', desc: 'Companies, subsidiaries, business units, sites, suppliers' },
            { name: 'Requirements', desc: 'Disclosure obligations from all frameworks' },
            { name: 'KPIs', desc: 'Metric definitions with values, targets, and validation' },
            { name: 'Evidence', desc: 'Documents, datasets, attestations, audit reports' },
            { name: 'Claims', desc: 'Auditable assertions with linked proof' },
            { name: 'Controls', desc: 'Control objectives, activities, and tests' },
            { name: 'Cases', desc: 'Incidents, grievances, remediation plans' }
          ]
        },
        {
          heading: 'Traceability Chain',
          chain: true,
          text: 'Every claim can be traced back through the complete evidence chain:',
          traceability: [
            'Claim → "We reduced Scope 1 emissions by 15%"',
            '↓ Requirements → ESRS E1-6, GRI 305-1, TCFD Metrics',
            '↓ KPI Values → Scope 1: 10,000 tCO2e (2024) vs 11,765 tCO2e (2023)',
            '↓ Evidence → Utility bills, meter readings, third-party verification',
            '↓ Source Data → Uploaded Excel from ERP system',
            '↓ Approvals → Data Analyst → Compliance Manager → Legal'
          ]
        },
        {
          heading: 'Impact Engine',
          text: 'When source KPI data changes, the Impact Engine shows all downstream effects: which requirements are impacted, which reports need updates, and which claims may need revision.'
        }
      ]
    },
    {
      id: 'evidence-audit',
      title: 'Evidence & Audit Packs',
      icon: '📁',
      content: [
        {
          heading: 'Evidence Vault',
          text: 'The Evidence Vault is your central repository for all compliance documentation, organized by framework, topic, and claim.'
        },
        {
          heading: 'Evidence Types',
          types: [
            { name: 'Policies', desc: 'Corporate policies, codes of conduct, procedures' },
            { name: 'Audit Reports', desc: 'Internal and external audit findings' },
            { name: 'Datasets', desc: 'Structured data files (Excel, CSV)' },
            { name: 'Attestations', desc: 'Management assertions, board approvals' },
            { name: 'Certificates', desc: 'ISO certifications, third-party verifications' },
            { name: 'Contracts', desc: 'Supplier agreements, SLAs, code of conduct clauses' },
            { name: 'Grievance Records', desc: 'Complaints, incident reports, remediation logs' }
          ]
        },
        {
          heading: 'Audit Pack Builder',
          text: 'Build audit-ready packages that bundle all evidence for specific frameworks or claims. Each pack includes:',
          packContents: [
            'Selected obligations with completion status',
            'Linked claims with assertion text',
            'Supporting evidence files',
            'Approval chain and timestamps',
            'Completeness score and gap analysis'
          ]
        },
        {
          heading: 'Audit Trail',
          text: 'Every action in Disclosure OS is logged with immutable audit trails: who changed what, when, and why. This satisfies CSRD Article 29 assurance requirements.'
        }
      ]
    },
    {
      id: 'integrations',
      title: 'Integrations',
      icon: '🔌',
      content: [
        {
          heading: 'ERP Connectors',
          text: 'Connect your enterprise systems to automatically sync ESG-relevant data.'
        },
        {
          heading: 'Supported ERPs',
          erps: [
            { name: 'SAP S/4HANA', data: 'Suppliers, Purchase Orders, Spend Data', status: 'Simulated' },
            { name: 'SAP Ariba Network', data: 'Suppliers, Contracts, Spend Data', status: 'Simulated' },
            { name: 'Coupa Procurement', data: 'Suppliers, Invoices, Spend Data', status: 'Simulated' }
          ]
        },
        {
          heading: 'Data Sync Features',
          features: [
            'Automatic supplier import with risk scoring',
            'Spend data for Scope 3 calculations',
            'Contract clauses for CSDDD compliance',
            'Purchase order tracking for value chain mapping'
          ]
        },
        {
          heading: 'Export Formats',
          formats: [
            { name: 'XBRL/iXBRL', desc: 'For ESRS digital filing requirements' },
            { name: 'PDF/DOCX', desc: 'Traditional report formats' },
            { name: 'Excel', desc: 'Data export for analysis' },
            { name: 'JSON/API', desc: 'Programmatic access' }
          ]
        }
      ]
    },
    {
      id: 'dashboard-templates',
      title: 'Dashboard Templates',
      icon: '📊',
      content: [
        {
          heading: 'Pre-Built Dashboards',
          text: 'Create dashboards instantly from 5 pre-configured templates designed for specific ESG use cases.'
        },
        {
          heading: 'Available Templates',
          dashboards: [
            { name: 'Carbon Footprint Dashboard', widgets: 5, desc: 'Track GHG emissions across all scopes with trends and breakdowns' },
            { name: 'Supply Chain Risk Dashboard', widgets: 5, desc: 'Monitor supplier risks, CSDDD compliance, and due diligence status' },
            { name: 'Compliance Progress Dashboard', widgets: 6, desc: 'Track framework coverage, evidence status, and reporting deadlines' },
            { name: 'Executive Summary Dashboard', widgets: 5, desc: 'High-level ESG overview for leadership with key metrics and trends' },
            { name: 'Data Quality Dashboard', widgets: 5, desc: 'Monitor data uploads, AI reviews, and pending approvals' }
          ]
        },
        {
          heading: 'Custom Dashboards',
          text: 'Build your own dashboards from 12 widget types including stat cards, line charts, bar charts, pie charts, and data tables. Drag-and-drop positioning with responsive layouts.'
        }
      ]
    },
    {
      id: 'faq',
      title: 'FAQ',
      icon: '❓',
      content: [
        {
          question: 'What makes Disclosure OS different from other ESG platforms?',
          answer: 'Disclosure OS is built as an operating system, not just a reporting tool. The defensibility flywheel (Upload → Map → Evidence → Claim → Audit Pack) creates legally defensible disclosures with complete traceability. Our crosswalk architecture means you enter data once and satisfy multiple frameworks automatically.'
        },
        {
          question: 'How does the AI review process work?',
          answer: 'When you upload documents, our AI extracts KPIs, classifies content by framework/topic, and suggests mappings with confidence scores. Items above 95% confidence can be auto-approved via workflow automation. Lower confidence items route to the AI Data Review queue for human approval (HITL - Human-in-the-Loop).'
        },
        {
          question: 'Can multiple users work on the same data?',
          answer: 'Yes! Disclosure OS supports real-time collaboration via WebSocket connections. Multiple users can work simultaneously with live updates. Edit locks prevent conflicts, and all changes are attributed to specific users in the audit trail.'
        },
        {
          question: 'How do I connect my ERP system?',
          answer: 'Navigate to Due Diligence OS → ERP Connectors. Currently supports SAP S/4HANA, SAP Ariba, and Coupa with simulated connections. Contact support for live integration setup with your enterprise credentials.'
        },
        {
          question: 'What is the Claims & Assertions feature?',
          answer: 'Claims are auditable statements you make in your reports (e.g., "We have 100% Tier 1 DD coverage"). Each claim links to specific requirements, KPI values, controls executed, and evidence bundles. This creates the defensibility chain auditors need.'
        },
        {
          question: 'How does the Impact Engine work?',
          answer: 'The Impact Engine visualizes downstream effects of KPI changes. When you modify source data, it shows which requirements are impacted, which reports need updates, and which claims may need revision. This prevents compliance gaps from data changes.'
        },
        {
          question: 'Can I export reports in XBRL format?',
          answer: 'Yes! Navigate to Disclosure Engine → XBRL Export. Create filings, add data points, validate against ESRS taxonomy, and generate iXBRL output. The system supports ESRS, SEC Climate, ESEF, and GRI taxonomies.'
        },
        {
          question: 'What roles should I assign to my team?',
          answer: 'Typical setup: Admin for IT/system owners, Compliance Manager for ESG leads, Data Analyst for data entry teams, Legal Counsel for legal review, Policy Adviser for governance, Auditor for external auditors (read-only), Viewer for stakeholders.'
        }
      ]
    },
    {
      id: 'best-practices',
      title: 'Best Practices',
      icon: '💡',
      content: [
        {
          heading: 'Data Management',
          practices: [
            'Use consistent naming conventions for uploaded files',
            'Tag all uploads with the relevant reporting period',
            'Review AI extractions within 48 hours to maintain data freshness',
            'Set up automated workflows for high-confidence extractions',
            'Use the Data Quality Dashboard to monitor completeness'
          ]
        },
        {
          heading: 'Compliance Workflow',
          practices: [
            'Start with Framework Coverage to identify gaps early',
            'Build claims incrementally as evidence becomes available',
            'Use the Requirements Tracker to monitor deadlines',
            'Set up Compliance Deadline Reminder workflows',
            'Generate audit packs before external auditor reviews'
          ]
        },
        {
          heading: 'CSDDD Due Diligence',
          practices: [
            'Import suppliers from ERP connectors for automatic risk scoring',
            'Focus DD efforts on high-risk and critical suppliers first',
            'Use the Supplier Portal for evidence collection at scale',
            'Document all grievances and remediation actions',
            'Run monthly Value Chain Risk reviews'
          ]
        },
        {
          heading: 'Reporting Cadence',
          cadence: [
            { frequency: 'Weekly', tasks: 'Review AI extraction queue, check workflow alerts' },
            { frequency: 'Monthly', tasks: 'Update KPIs, run Impact Engine analysis, review supplier risks' },
            { frequency: 'Quarterly', tasks: 'Generate progress reports, board updates, gap analysis' },
            { frequency: 'Annually', tasks: 'CSRD filing, full audit pack preparation, framework crosswalk review' }
          ]
        }
      ]
    }
  ];

  const filteredSections = sections.filter(section => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return section.title.toLowerCase().includes(query) ||
           JSON.stringify(section.content).toLowerCase().includes(query);
  });

  // Quick reference cards for the overview tab
  const quickReferenceCards = [
    { title: 'Core Workflow', icon: Zap, color: 'emerald', items: ['Upload', 'Map', 'Evidence', 'Claim', 'Audit Pack'] },
    { title: 'Frameworks', icon: Shield, color: 'blue', items: ['CSRD/ESRS', 'CSDDD', 'EU Taxonomy', 'GRI', 'SASB', 'TCFD', 'SBTi', 'CDP'] },
    { title: 'User Roles', icon: Users, color: 'purple', items: ['Admin', 'Compliance Manager', 'Data Analyst', 'Legal Counsel', 'Policy Adviser', 'Auditor', 'Viewer'] },
    { title: 'Automation', icon: Bot, color: 'orange', items: ['12 Workflow Templates', '9 Trigger Types', '14 Action Types', '8 Categories'] }
  ];

  return (
    <div className="space-y-6" data-testid="user-guide-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl p-8 text-white">
        <div className="flex items-center gap-3 mb-2">
          <BookOpen size={36} />
          <div>
            <h1 className="text-3xl font-bold">Help & Resources</h1>
            <p className="text-slate-300">Comprehensive documentation for Disclosure OS</p>
          </div>
        </div>
        
        {/* Tab Navigation */}
        <div className="flex gap-2 mt-6">
          {['guide', 'architecture', 'journeys', 'automation'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab
                  ? 'bg-white text-slate-900'
                  : 'bg-white/10 text-white hover:bg-white/20'
              }`}
            >
              {tab === 'guide' && 'User Guide'}
              {tab === 'architecture' && 'Architecture'}
              {tab === 'journeys' && 'User Journeys'}
              {tab === 'automation' && 'Automation'}
            </button>
          ))}
        </div>
      </div>

      {/* Quick Reference Cards */}
      {activeTab === 'guide' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickReferenceCards.map((card, idx) => (
            <div key={idx} className={`bg-${card.color}-50 border border-${card.color}-200 rounded-xl p-4`}>
              <div className="flex items-center gap-2 mb-3">
                <card.icon className={`h-5 w-5 text-${card.color}-600`} />
                <h3 className={`font-semibold text-${card.color}-900`}>{card.title}</h3>
              </div>
              <div className="flex flex-wrap gap-1">
                {card.items.map((item, i) => (
                  <Badge key={i} variant="secondary" className="text-xs">
                    {item}
                  </Badge>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm border p-4">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
            <input
              type="text"
              placeholder="Search documentation..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              data-testid="search-input"
            />
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
        <h2 className="font-semibold text-emerald-900 mb-3 flex items-center gap-2">
          <Zap className="h-4 w-4" />
          Quick Links
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {['getting-started', 'frameworks', 'user-journeys', 'workflow-automation', 'data-model', 'faq'].map((section) => (
            <button
              key={section}
              onClick={() => {
                setExpandedSection(section);
                setActiveTab('guide');
              }}
              className="text-sm text-emerald-700 hover:text-emerald-900 text-left hover:underline"
            >
              → {sections.find(s => s.id === section)?.title}
            </button>
          ))}
        </div>
      </div>

      {/* Documentation Sections */}
      <div className="space-y-4">
        {filteredSections.map((section) => (
          <div key={section.id} className="bg-white rounded-xl shadow-sm border overflow-hidden" data-testid={`section-${section.id}`}>
            <button
              onClick={() => toggleSection(section.id)}
              className="w-full flex items-center justify-between p-5 hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl">{section.icon}</span>
                <h2 className="text-lg font-bold text-slate-900">{section.title}</h2>
              </div>
              {expandedSection === section.id ? (
                <ChevronDown className="text-slate-400" size={24} />
              ) : (
                <ChevronRight className="text-slate-400" size={24} />
              )}
            </button>

            {expandedSection === section.id && (
              <div className="px-5 pb-5 space-y-6 border-t border-slate-100 pt-5">
                {section.content.map((item, idx) => (
                  <div key={idx}>
                    {item.heading && (
                      <h3 className="text-base font-semibold text-slate-900 mb-3 flex items-center gap-2">
                        {item.highlight && <Zap className="h-4 w-4 text-amber-500" />}
                        {item.heading}
                      </h3>
                    )}
                    
                    {item.text && (
                      <p className="text-slate-600 mb-3 leading-relaxed">{item.text}</p>
                    )}

                    {/* Workflow highlight */}
                    {item.workflow && (
                      <div className="flex items-center gap-2 my-4 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg border border-emerald-200">
                        {item.workflow.map((step, i) => (
                          <React.Fragment key={i}>
                            <div className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-sm font-medium">
                              {step}
                            </div>
                            {i < item.workflow.length - 1 && (
                              <ChevronRight className="text-emerald-400" size={20} />
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    )}

                    {item.steps && (
                      <ol className="list-decimal list-inside space-y-2 text-slate-600">
                        {item.steps.map((step, i) => (
                          <li key={i} className="leading-relaxed">{typeof step === 'string' ? step : `${step.step}: ${step.desc}`}</li>
                        ))}
                      </ol>
                    )}

                    {/* Layers for architecture */}
                    {item.layers && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {item.layers.map((layer, i) => (
                          <div key={i} className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                            <div className="font-medium text-slate-900">{layer.name}</div>
                            <div className="text-sm text-slate-500">{layer.desc}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Services list */}
                    {item.services && (
                      <ul className="space-y-2">
                        {item.services.map((service, i) => (
                          <li key={i} className="flex items-start gap-2 text-slate-600">
                            <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                            <span>{service}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* Roles */}
                    {item.roles && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {item.roles.map((role, i) => (
                          <div key={i} className={`p-3 rounded-lg border-l-4 border-${role.color}-500 bg-${role.color}-50`}>
                            <div className="font-medium text-slate-900">{role.name}</div>
                            <div className="text-sm text-slate-600">{role.permissions}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Frameworks */}
                    {item.frameworks && (
                      <div className="space-y-2">
                        {item.frameworks.map((fw, i) => (
                          <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div>
                              <div className="font-medium text-slate-900">{fw.name}</div>
                              <div className="text-sm text-slate-500">{fw.desc}</div>
                            </div>
                            <Badge variant={fw.status === 'Full Support' ? 'default' : 'secondary'}>
                              {fw.status}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Journey steps */}
                    {item.journey && item.steps && (
                      <div className="space-y-2">
                        {item.steps.map((step, i) => (
                          <div key={i} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                            <div className="w-6 h-6 rounded-full bg-emerald-600 text-white text-xs flex items-center justify-center flex-shrink-0">
                              {i + 1}
                            </div>
                            <div>
                              <div className="font-medium text-slate-900">{step.step}</div>
                              <div className="text-sm text-slate-500">{step.desc}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Workflow categories */}
                    {item.categories && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {item.categories.map((cat, i) => (
                          <div key={i} className="p-3 bg-slate-50 rounded-lg">
                            <div className="font-medium text-slate-900 mb-2">{cat.name}</div>
                            <div className="flex flex-wrap gap-1">
                              {cat.templates.map((t, j) => (
                                <Badge key={j} variant="outline" className="text-xs">{t}</Badge>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Triggers/Actions list */}
                    {(item.triggers || item.actions) && (
                      <ul className="space-y-1">
                        {(item.triggers || item.actions).map((t, i) => (
                          <li key={i} className="flex items-start gap-2 text-slate-600 text-sm">
                            <Zap className="h-3 w-3 text-amber-500 mt-1 flex-shrink-0" />
                            <span>{t}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* Core objects */}
                    {item.objects && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {item.objects.map((obj, i) => (
                          <div key={i} className="p-3 bg-slate-50 rounded-lg">
                            <div className="font-medium text-slate-900">{obj.name}</div>
                            <div className="text-sm text-slate-500">{obj.desc}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Traceability chain */}
                    {item.chain && item.traceability && (
                      <div className="bg-slate-900 rounded-lg p-4 font-mono text-sm">
                        {item.traceability.map((line, i) => (
                          <div key={i} className={`${line.startsWith('↓') ? 'text-emerald-400 ml-4' : 'text-white'}`}>
                            {line}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Evidence types */}
                    {item.types && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {item.types.map((type, i) => (
                          <div key={i} className="p-3 bg-slate-50 rounded-lg">
                            <div className="font-medium text-slate-900">{type.name}</div>
                            <div className="text-sm text-slate-500">{type.desc}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Pack contents */}
                    {item.packContents && (
                      <ul className="space-y-2">
                        {item.packContents.map((content, i) => (
                          <li key={i} className="flex items-start gap-2 text-slate-600">
                            <FileBarChart className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                            <span>{content}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* ERPs */}
                    {item.erps && (
                      <div className="space-y-2">
                        {item.erps.map((erp, i) => (
                          <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div>
                              <div className="font-medium text-slate-900">{erp.name}</div>
                              <div className="text-sm text-slate-500">{erp.data}</div>
                            </div>
                            <Badge variant="outline">{erp.status}</Badge>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Features list */}
                    {item.features && !item.layers && (
                      <ul className="space-y-2">
                        {item.features.map((feature, i) => (
                          <li key={i} className="flex items-start gap-2 text-slate-600">
                            <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* Export formats */}
                    {item.formats && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {item.formats.map((format, i) => (
                          <div key={i} className="p-3 bg-slate-50 rounded-lg text-center">
                            <div className="font-medium text-slate-900">{format.name}</div>
                            <div className="text-xs text-slate-500">{format.desc}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Dashboard templates */}
                    {item.dashboards && (
                      <div className="space-y-2">
                        {item.dashboards.map((dash, i) => (
                          <div key={i} className="p-3 bg-slate-50 rounded-lg">
                            <div className="flex items-center justify-between">
                              <div className="font-medium text-slate-900">{dash.name}</div>
                              <Badge variant="secondary">{dash.widgets} widgets</Badge>
                            </div>
                            <div className="text-sm text-slate-500 mt-1">{dash.desc}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* FAQ */}
                    {item.question && (
                      <div className="bg-slate-50 p-4 rounded-lg">
                        <div className="font-medium text-slate-900 mb-2">Q: {item.question}</div>
                        <div className="text-slate-600">A: {item.answer}</div>
                      </div>
                    )}

                    {/* Best practices */}
                    {item.practices && !item.cadence && (
                      <ul className="space-y-2">
                        {item.practices.map((practice, i) => (
                          <li key={i} className="flex items-start gap-2 text-slate-600">
                            <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                            <span>{practice}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* Cadence */}
                    {item.cadence && (
                      <div className="space-y-2">
                        {item.cadence.map((c, i) => (
                          <div key={i} className="p-3 border-l-4 border-emerald-500 bg-emerald-50 pl-4">
                            <div className="font-medium text-slate-900">{c.frequency}</div>
                            <div className="text-sm text-slate-600">{c.tasks}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="bg-slate-100 border border-slate-200 rounded-xl p-6 text-center">
        <p className="text-slate-600 mb-3">Need additional support?</p>
        <div className="flex items-center justify-center gap-4">
          <a href="mailto:support@disclosureos.com" className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-800 font-medium">
            <ExternalLink size={18} />
            Contact Support
          </a>
        </div>
      </div>
    </div>
  );
};

export default UserGuide;
